import React, { useEffect, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { createRoot } from "react-dom/client";
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";

import "./index.css";
import { App } from "./app/index";
import { IS_LOCALHOST, updateVariables } from "./constants/Config";
import theme from "./styles/mui/theme";
import store from "./redux/ConfigureStore";
import { loginScope, msalConfig } from "./utils/authentication/authConfig";
import { registerServiceWorker } from "./utils/serviceWorkerRegistration";
import { startVersionCheck } from "./utils/appVersionCheck";
import { GetEnvVariables } from "./utils";

const container = document.getElementById("root")!;
const root = createRoot(container);

export let msalInstance: PublicClientApplication;
export let loginRequest: any;

const AppWithAuthentication = () => {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
 const variables = useMemo<Record<string, any>>(() => {
   const envVariables = {
      REACT_APP_PORTAL_REPLACE_START: "[_APP_REPLACE_START_]",
      REACT_APP_PORTAL_HOST_CONFIG: "[_PORTAL_APP_HOST_CONFIG_]",
      REACT_APP_PORTAL_CLIENT: "[_PORTAL_APP_CLIENT_]",
      REACT_APP_PORTAL_LOGINURL: "[_PORTAL_APP_LOGINURL_]",
      REACT_APP_PORTAL_KNOWN_AUTHORIETIES: "[_PORTAL_APP_KNOWN_AUTHORIETIES_]",
      REACT_APP_PORTAL_REDIRECT_URI: "[_PORTAL_APP_REDIRECT_URI_]",
      REACT_APP_PORTAL_B2C_SCOPE: "[_PORTAL_APP_B2C_SCOPE_]",
      REACT_APP_PORTAL_GOOGLE_MAP_KEY: "[_PORTAL_APP_GOOGLE_MAP_KEY_]",
      REACT_APP_PORTAL_REPLACE_END: "[_APP_REPLACE_END_]",
      REACT_APP_PORTAL_SAML_CALLBACK: "[_HPP_HOST_CONFIG_SAML_CALLBACK_]"
    };
    return IS_LOCALHOST ? GetEnvVariables() as unknown as Record<string, any> : envVariables;
  }, []);
   useEffect(() => {
    (async () => {
        updateVariables(variables);
        msalInstance = new PublicClientApplication(msalConfig(variables));
        await msalInstance.initialize();
        msalInstance.enableAccountStorageEvents();

        const searchCode = new URLSearchParams(window.location.search).get("code");
        const hashCode = new URLSearchParams(window.location.hash.replace("#", "")).get("code");
        const authCode = searchCode || hashCode;

        let redirectResult = null;
        if (authCode) {
          const codeHandledKey = `msal_auth_code_handled_${authCode}`;
          if (!sessionStorage.getItem(codeHandledKey)) {
            sessionStorage.setItem(codeHandledKey, "true");
            redirectResult = await msalInstance.handleRedirectPromise();
          }
        } else {
          redirectResult = await msalInstance.handleRedirectPromise();
        }

        if (redirectResult?.account) {
          msalInstance.setActiveAccount(redirectResult.account);
        }

        const existingAccounts = msalInstance.getAllAccounts();
        if (existingAccounts.length > 0 && !msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(existingAccounts[0]);
        }

        msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const loginResult: any = event.payload;
            if (loginResult?.account) {
              msalInstance.setActiveAccount(loginResult.account);
            }
          }
        });
        loginRequest = loginScope();
        setIsConfigLoaded(true);
    })();
  }, []);

  return (
    <Box>
      {isConfigLoaded && msalInstance ? (
        <React.StrictMode>
          <MsalProvider instance={msalInstance}>
            <Provider store={store}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <App instance={msalInstance} />
              </ThemeProvider>
            </Provider>
          </MsalProvider>
        </React.StrictMode>
      ) : (
        <>Loading...</>
      )}
    </Box>
  );
};

// Register service worker and start version polling for auto-reload on new deployments
registerServiceWorker();
startVersionCheck();

root.render(<AppWithAuthentication />);
